import React, { useState, useEffect, useRef } from 'react';

const VideoTimeline = ({ 
  currentTime, 
  duration, 
  onSeek, 
  isPlaying, 
  onPlayPause,
  telemetryData = [],
  onTimeChange,
  dashboardId,
  style = {}
}) => {
  const timelineRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Handle timeline click
  const handleTimelineClick = (e) => {
    if (!timelineRef.current || duration <= 0) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercentage = clickX / rect.width;
    const seekTime = clickPercentage * duration;
    
    onSeek(seekTime);
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging || !timelineRef.current || duration <= 0) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
    const time = percentage * duration;
    
    setDragTime(time);
    onTimeChange(time);
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (isDragging) {
      onSeek(dragTime);
      setIsDragging(false);
    }
  };

  // Handle mouse down
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragTime(currentTime);
    handleTimelineClick(e);
  };

  // Add event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, duration]);

  // Find telemetry points for timeline markers
  const getTelemetryMarkers = () => {
    if (!telemetryData.length || duration <= 0) return [];
    
    const markers = [];
    const step = duration / 20; // Show up to 20 markers
    
    for (let i = 0; i < duration; i += step) {
      const point = telemetryData.find(p => Math.abs(p.time - i) < step / 2);
      if (point) {
        markers.push({
          time: point.time,
          percentage: (point.time / duration) * 100,
          hasData: true
        });
      }
    }
    
    return markers;
  };

  const telemetryMarkers = getTelemetryMarkers();

  return (
    <div 
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px',
        background: 'rgba(0,0,0,0.8)',
        borderRadius: '8px',
        ...style
      }}
    >
      {/* Play/Pause Button */}
      <button
        onClick={onPlayPause}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '20px',
          cursor: 'pointer',
          padding: '5px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '30px',
          minHeight: '30px'
        }}
      >
        {isPlaying ? '⏸️' : '▶️'}
      </button>

      {/* Time Display */}
      <div style={{ color: 'white', fontSize: '12px', minWidth: '80px' }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        style={{
          flex: 1,
          height: '20px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '10px',
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Progress Bar */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${progress}%`,
            background: '#007bff',
            borderRadius: '10px',
            transition: isDragging ? 'none' : 'width 0.1s ease'
          }}
        />

        {/* Telemetry Markers */}
        {telemetryMarkers.map((marker, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${marker.percentage}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '4px',
              height: '4px',
              background: marker.hasData ? '#28a745' : '#6c757d',
              borderRadius: '50%',
              pointerEvents: 'none'
            }}
          />
        ))}

        {/* Current Time Indicator */}
        <div
          style={{
            position: 'absolute',
            left: `${progress}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '12px',
            height: '12px',
            background: 'white',
            borderRadius: '50%',
            border: '2px solid #007bff',
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}
        />
      </div>

      {/* Speed Control */}
      <select
        style={{
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.3)',
          color: 'white',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '12px'
        }}
        onChange={(e) => {
          // Handle speed change
          console.log('Speed changed to:', e.target.value);
        }}
      >
        <option value="0.5">0.5x</option>
        <option value="1">1x</option>
        <option value="1.25">1.25x</option>
        <option value="1.5">1.5x</option>
        <option value="2">2x</option>
      </select>
    </div>
  );
};

// Helper function to format time
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default VideoTimeline;




